import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { configureForm } from '@thinkmill/pragmatic-forms';

import { encode, describe } from 'inside-out/managers/formManager';
import { Link } from 'inside-out/components/shared/link';

import {
	detailSideBarLayout,
	formSection,
	contentSection,
	button,
	pink,
} from 'inside-out/global-styles';

import {
	wrapperStyle,
	innerWrapperStyle,
	titleStyle,
	textStyle,
	holdAFundraiserFormWrapperStyle,
	holdAFundraiserTextareaStyle,
} from 'inside-out/page-styles/get-involved/detail';

export default class HoldAFundraiserPage extends React.PureComponent {
	static propTypes = {
		data: PropTypes.shape({
			page: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							heading: PropTypes.string.isRequired,
							body: PropTypes.string.isRequired,
						}).isRequired,
					})
				).isRequired,
			}).isRequired,
		}).isRequired,
	};

	state = {
		hasSubmitted: false,
	};

	HoldAFundraiserForm = configureForm({
		initFields: () => ({
			'form-name': 'hold-a-fundraiser',
			botField: '',
			firstName: '',
			lastName: '',
			email: '',
			message: '',
		}),
		validate: ({ firstName, lastName, email, message }) => {
			const errors = {};
			if (!firstName) errors.firstName = 'Please enter your First Name';
			if (!lastName) errors.lastName = 'Please enter your Last Name';
			if (!email) errors.email = 'Please enter an Email Address';
			if (!message) errors.message = 'Please enter a Message';
			return errors;
		},
		submit: async formData => {
			try {
				await fetch('/', {
					method: 'POST',
					body: encode(formData),
				});

				this.setState({ hasSubmitted: true });
			} catch (error) {
				console.error(error);
			}
		},
	})(({ form }) => (
		<form.Form netlify="true" name="hold-a-fundraiser" netlify-honeypot="botField">
			<div className={formSection.wrapper}>
				<p style={{ display: 'none' }}>
					<label>
						Don’t fill this out if you&apos;re human:
						<input {...form.getInputProps({ name: 'botField', type: 'text' })} />
					</label>
				</p>
				<div aria-live="assertive">
					{/* Errors that come back from the server */}
					{form.submitError && (
						<p className={formSection.validationMessage}>Error: {form.submitError}</p>
					)}
					{/* Field Validation Errors */}
					{form.hasErrors && (
						<ul className={formSection.validationSummary}>
							{Object.keys(form.errors).map(key => (
								<li key={key} className={formSection.validationMessage}>{`${form.errors[key]}`}</li>
							))}
						</ul>
					)}
				</div>
				<div className={formSection.halfWidthWrapper}>
					<div className={formSection.field}>
						{form.errors['firstName'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['firstName']}</p>
						)}
						<label htmlFor="firstName">First Name</label>
						<input id="firstName" {...form.getInputProps({ name: 'firstName', type: 'text' })} />
					</div>

					<div className={formSection.field}>
						{form.errors['lastName'] && (
							<p className={formSection.validationFieldMessage}>{form.errors['lastName']}</p>
						)}
						<label htmlFor="lastName">Last Name</label>
						<input id="lastName" {...form.getInputProps({ name: 'lastName', type: 'text' })} />
					</div>
				</div>

				<div className={formSection.field}>
					{form.errors['email'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['email']}</p>
					)}
					<label htmlFor="emailAddress">Email Address</label>
					<input id="emailAddress" {...form.getInputProps({ name: 'email', type: 'text' })} />
				</div>

				<div className={formSection.field}>
					{form.errors['message'] && (
						<p className={formSection.validationFieldMessage}>{form.errors['message']}</p>
					)}
					<label htmlFor="message">Message</label>
					<textarea
						className={holdAFundraiserTextareaStyle}
						id="message"
						{...form.getInputProps({ name: 'message' })}
					/>
				</div>

				<button
					className={formSection.submitButton}
					type="submit"
					disabled={form.hasErrors || form.isLoading}
				>
					Send Message
				</button>
			</div>
		</form.Form>
	));

	render() {
		const pageSettings = this.props.data.page.edges[0].node;

		return (
			<section className={wrapperStyle}>
				<Helmet title="Hold a Fundraiser">
          <meta property="og:type" content="website"/>
          <meta property="og:title" content="Hold a Fundraiser"/>
          <meta name="description" content="Fundraising for InsideOut is a great way to make a tangible difference to the lives of Australians living with, or affect by eating disorders."/>
          <meta property="og:description" content="Fundraising for InsideOut is a great way to make a tangible difference to the lives of Australians living with, or affect by eating disorders."/>
        </Helmet>

				<div className={innerWrapperStyle}>
					<div className={detailSideBarLayout.wrapper}>
						<div className={detailSideBarLayout.leftSide}>
							<div className={contentSection('#fff')}>
								{!this.state.hasSubmitted && (
									<React.Fragment>
										<header>
											<h1 className={titleStyle}>{pageSettings.heading}</h1>
										</header>
										<div
											className={textStyle}
											dangerouslySetInnerHTML={{ __html: pageSettings.body }}
										/>
										<div className={holdAFundraiserFormWrapperStyle}>
											<this.HoldAFundraiserForm />
										</div>
									</React.Fragment>
								)}
								{this.state.hasSubmitted && (
									<React.Fragment>
										<header>
											<h1 className={titleStyle}>{pageSettings.thank_you_heading}</h1>
										</header>
										<div
											className={textStyle}
											dangerouslySetInnerHTML={{
												__html: pageSettings.thank_you_body,
											}}
										/>
										<div className={contentSection('#fff')}>
											<Link className={button(pink)} to={pageSettings.thank_you_button_url}>
												{pageSettings.thank_you_button_text}
											</Link>
										</div>
									</React.Fragment>
								)}
							</div>
						</div>
					</div>
				</div>
			</section>
		);
	}
}

export const query = graphql`
	query HoldAFundraiserPageQuery {
		page: allContentJson(
			filter: { id: { regex: "/content\\/get-involved-hold-a-fundraiser-page\\.json/" } }
		) {
			edges {
				node {
					heading
					body
					thank_you_heading
					thank_you_body
					thank_you_button_text
					thank_you_button_url
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/pages/get-involved/hold-a-fundraiser/index.js